import {IVolumeLeague, IVolumeLeagueMarket, LeagueKind} from '../../apis/volume_league/list'
import {MARKET_SUPPORT} from './constants'

// 숫자 배열 중복 제거 (target<es2015 라 Set 스프레드 불가)
export const uniq = (arr: number[]): number[] => arr.filter((v, i) => arr.indexOf(v) === i)

// window_days → 기간 라벨
export const tfLabel = (wd?: number | null): string => (wd === 1 ? '24H' : wd ? `${wd}D` : '-')

// 이름에서 후행 기간 라벨 제거 ("Tenbagger 24H" → "Tenbagger")
export const stripTf = (name?: string | null): string => (name || '').replace(/\s+(24H|\d+\s*D)$/i, '').trim()

// Competition·Rolling 공통 그룹 키. symbol 단위로만 묶음 (symbol 없으면 '-').
export const groupOf = (l?: IVolumeLeague): string => {
    const base = l?.symbol || ''
    return base.toUpperCase() || '-'
}

// 종류 태그 배경 + ring(inset) 색상 — 상시 순위표(초록) / 기간 대회(파랑)로 한눈에 구분
export const kindTagClass = (kind: LeagueKind): string =>
    kind === 'rolling'
        ? 'bg-tag_green_light text-font_green ring-1 ring-inset ring-primary/40'
        : 'bg-blue-50 text-sub_blue ring-1 ring-inset ring-sub_blue/40'

// 절대 http/https URL (최대 512자)
export const isValidExchangeUrl = (url?: string): boolean => {
    const s = (url || '').trim()
    if (!s || s.length > 512) return false
    return /^https?:\/\/.+/i.test(s)
}

// markets JSON 텍스트 → 시장 목록. 서버(ParseVolumeLeagueMarkets)와 같은 규칙으로 미리 검사해
// 저장 전에 무엇이 틀렸는지 보여준다. 최종 판단은 서버.
export const parseMarkets = (text: string): {markets?: IVolumeLeagueMarket[]; error?: string} => {
    if (!text.trim()) return {error: '시장 목록이 비어 있습니다.'}
    let raw: unknown
    try {
        raw = JSON.parse(text)
    } catch (e) {
        return {error: `JSON 형식 오류: ${(e as Error).message}`}
    }
    if (!Array.isArray(raw) || raw.length === 0) return {error: '배열 형태로 1개 이상 입력하세요.'}
    const out: IVolumeLeagueMarket[] = []
    const seen: string[] = []
    for (let i = 0; i < raw.length; i++) {
        const r = raw[i] as {[k: string]: unknown}
        const at = `${i + 1}번째`
        if (!r || typeof r !== 'object') return {error: `${at}: 객체가 아닙니다.`}
        const exchange = String(r.exchange ?? '')
            .trim()
            .toLowerCase()
        const type = String(r.type ?? '')
            .trim()
            .toLowerCase()
        const symbol = String(r.symbol ?? '')
            .trim()
            .toUpperCase()
        const quote = String(r.quote ?? '')
            .trim()
            .toUpperCase()
        const types = MARKET_SUPPORT[exchange]
        if (!types || types.indexOf(type as 'spot' | 'futures') < 0) {
            return {error: `${at}: 지원하지 않는 시장 (${exchange || '-'} ${type || '-'})`}
        }
        if (!symbol) return {error: `${at}: symbol 필수`}
        if (type === 'spot' && !quote) return {error: `${at}: 현물은 quote 필수 (예: KRW, USDT)`}
        if (type === 'futures' && quote) return {error: `${at}: 선물은 quote 를 비우고 symbol 에 계약명 (예: BILLUSDT)`}
        const key = `${exchange}|${type}|${symbol}|${quote}`
        if (seen.indexOf(key) >= 0) return {error: `${at}: 중복된 시장`}
        seen.push(key)
        out.push(type === 'spot' ? {exchange, type: 'spot', symbol, quote} : {exchange, type: 'futures', symbol})
    }
    return {markets: out}
}

// 시장 목록의 거래소 (입력 순서 유지, 중복 제거)
export const marketExchanges = (markets?: IVolumeLeagueMarket[]): string[] =>
    (markets || []).map(m => m.exchange).filter((v, i, a) => a.indexOf(v) === i)

// 리그의 markets → 편집용 JSON 텍스트
export const marketsToText = (markets?: IVolumeLeagueMarket[] | null): string =>
    markets && markets.length > 0 ? JSON.stringify(markets, null, 2) : ''
